import React, { useState, useEffect, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Nav } from "react-bootstrap";
import { useAuth } from "./shared/AuthContext";
import logo from "../pages/images/logonoor_circle.png";

interface SidebarComponentProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  icon: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/dashboard", icon: "bi-speedometer2", label: "Dashboard" },
  { path: "/products", icon: "bi-box", label: "Products" },
  { path: "/orders", icon: "bi-table", label: "Orders" },
  { path: "/orders/new", icon: "bi-plus-square", label: "New Order" },
  { path: "/ingredients", icon: "bi-clipboard", label: "Ingredients" },
  { path: "/summary", icon: "bi-calendar3", label: "Summary" },
];

const SIDEBAR_WIDTH_EXPANDED = 270;
const SIDEBAR_WIDTH_COLLAPSED = 74;
const MOBILE_BREAKPOINT = "(max-width: 768px)";

const SidebarComponent: React.FC<SidebarComponentProps> = ({ children }) => {
  const [expanded, setExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
      setExpanded(!e.matches);
    };
    handleMediaChange(mediaQuery);
    mediaQuery.addEventListener("change", handleMediaChange);
    return () => mediaQuery.removeEventListener("change", handleMediaChange);
  }, []);

  useEffect(() => {
    if (isMobile) setExpanded(false);
  }, [location.pathname, isMobile]);

  const handleNavClick = (path: string) => navigate(path);
  const toggleSidebar = () => setExpanded(!expanded);
  const handlePusherClick = () => {
    if (isMobile && expanded) setExpanded(false);
  };

  const sidebarWidth = expanded
    ? SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH_COLLAPSED;
  const sidebarVisible = isMobile ? expanded : true;

  return (
    <div className="d-flex min-vh-100 bg-body-tertiary">
      <div className="d-flex align-items-start flex-shrink-0">
        <aside
          className="bg-white border-end shadow-sm flex-shrink-0 overflow-hidden overflow-y-auto position-relative"
          style={{
            width: sidebarVisible ? `${sidebarWidth}px` : 0,
            minWidth: sidebarVisible ? `${sidebarWidth}px` : 0,
            transition: "width 0.25s ease, min-width 0.25s ease",
          }}
        >
          <div className="d-flex flex-column h-100 p-3 gap-3">
            <div className="d-flex flex-column align-items-center justify-content-center px-1">
              <img src={logo} alt="Noor Bakery" style={{ height: expanded ? 40 : 30, width: expanded ? 40 : 30 }} />
              {expanded && (
                <span className="fw-bold text-dark mt-1" style={{ fontSize: "13px" }}>
                  {user?.fullname || user?.username || "Supplier"}
                </span>
              )}
            </div>

            <Nav className="flex-column gap-2 flex-grow-1">
              {NAV_ITEMS.map(({ path, icon, label }) => {
                const isActive =
                  location.pathname === path ||
                  (path === "/orders" &&
                    location.pathname.startsWith("/orders/") &&
                    !location.pathname.startsWith("/orders/new")) ||
                  (path !== "/dashboard" &&
                    path !== "/orders" &&
                    location.pathname.startsWith(`${path}/`));
                return (
                  <Nav.Link
                    key={path}
                    onClick={() => handleNavClick(path)}
                    className={`d-flex align-items-center gap-3 rounded-4 px-3 py-3 text-decoration-none ${
                      isActive
                        ? "active bg-primary-subtle text-primary-emphasis border border-primary-subtle fw-semibold shadow-sm"
                        : "text-body border border-transparent"
                    } ${expanded ? "justify-content-start" : "justify-content-center"}`}
                  >
                    <i className={`bi ${icon}`}></i>
                    {expanded && <span>{label}</span>}
                  </Nav.Link>
                );
              })}
            </Nav>

            <div className="border-top pt-3 mt-auto">
              <Nav.Link
                onClick={logout}
                className={`d-flex align-items-center gap-3 rounded-4 px-3 py-3 text-decoration-none text-body ${
                  expanded ? "justify-content-start" : "justify-content-center"
                }`}
              >
                <i className="bi bi-box-arrow-right"></i>
                {expanded && <span>Logout</span>}
              </Nav.Link>
            </div>
          </div>
        </aside>

        {!isMobile && (
          <div className="ms-2 mt-3 flex-shrink-0">
            <button
              type="button"
              onClick={toggleSidebar}
              className="btn btn-light border rounded-circle shadow-sm p-2 d-inline-flex align-items-center justify-content-center"
              aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              <i className="bi bi-list"></i>
            </button>
          </div>
        )}
      </div>

      <div className="d-flex flex-column flex-grow-1 overflow-hidden position-relative">
        {isMobile && (
          <div className="position-absolute top-0 start-0 z-3 ms-2 mt-2">
            <button
              type="button"
              onClick={toggleSidebar}
              className="btn btn-light border rounded-circle shadow-sm p-2 d-inline-flex align-items-center justify-content-center"
              aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              <i className="bi bi-list"></i>
            </button>
          </div>
        )}

        <div
          onClick={handlePusherClick}
          className="flex-grow-1 overflow-auto position-relative"
        >
          {isMobile && expanded && (
            <div
              className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark bg-opacity-25"
              style={{ zIndex: 100 }}
            />
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default SidebarComponent;
